<template>
  <div class="modal-dialog modal-dialog-centered modal-xl modal-fullscreen-sm-down">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">
          <template v-if="isEdit">
            Edit
          </template>
          <template v-else>
            Add
          </template>
          Class
        </h5>
        <button
          type="button"
          class="btn-close"
          aria-label="Close"
          @click="close"
        />
      </div>

      <div class="modal-body">
        <NavTabs
          v-if="isEdit"
          v-model="activeTab"
          :tabs="tabs"
          class="mb-3"
        />
        <SchoolClassInfo
          v-if="activeTab == 'classInfo'"
          v-model="schoolClass"
          :input-errors="inputErrors"
        />
        <SchoolClassEvents
          v-if="activeTab == 'events'"
          :school-class="schoolClass"
        />
      </div>

      <div class="modal-footer">
        <div v-if="inputErrors['non_field_errors']" class="w-100">
          <div class="alert alert-danger p-1 mt-3 mb-0" role="alert">
            {{ inputErrors["non_field_errors"] }}
          </div>
        </div>
        <div v-if="showOnlyOneStudentWarning" class="w-100">
          <div class="alert alert-warning p-1 mb-0" role="alert">
            This class is a group class but it only has one student. <br>
            Check if it is supposed to be an individual class.
          </div>
        </div>
        <div v-if="showManyStudentWarning" class="w-100">
          <div class="alert alert-warning p-1 mb-0" role="alert">
            This class is an individual class but it has more than one student. <br>
            Check if it is supposed to be a group class (change Class Format to Group).
          </div>
        </div>
        <div v-if="isEdit && userStore.isCurrentTeacherAdmin" class="me-auto">
          <button type="button" class="btn btn-danger" @click="confirmDeleteClass">
            Delete
          </button>
        </div>
        <button type="button" class="btn btn-secondary" @click="close">
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="loading"
          @click="save"
        >
          <span
            v-if="loading"
            class="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          />
          Save
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { api } from "@/api";
import mappingsMixin from "@/mixins/mappings.mixin";
import { IInputErrors } from "@/types/input";
import ISchoolClass from "@/types/school_class";
import { useUserStore } from "@/stores/user.store";
import { useCalendarStore } from "@/stores/calendar.store";
import timeMixin from "@/mixins/time.mixin";
import { useToastStore } from "@/stores/toast.store";
import { useModalStore } from "@/stores/modal.store";
import { ref } from "vue";
import SchoolClass from "@/types/models/school_class";
import { useStudentStore } from "@/stores/student.store";
import setsMixin from "@/mixins/sets.mixin";
import NavTabs from "@/components/ui/nav_tabs/NavTabs.vue";
import SchoolClassInfo from "@/components/modals/calendar/school_class/tabs/SchoolClassInfo.vue";
import SchoolClassEvents from "@/components/modals/calendar/school_class/tabs/SchoolClassEvents.vue";

export default defineComponent({
  name: "SchoolClassModal",
  components: {
    SchoolClassInfo,
    SchoolClassEvents,
    NavTabs,
  },
  mixins: [mappingsMixin, timeMixin, setsMixin],
  props: {
    schoolClassProp: {
      type: Object as PropType<SchoolClass>,
      default: () => {
        return null;
      },
    },
  },
  emits: ["close"],
  setup() {
    const userStore = useUserStore();
    const studentStore = useStudentStore();
    const calendarStore = useCalendarStore();
    const toastStore = useToastStore();
    const modalStore = useModalStore();
    const inputErrors = ref({} as IInputErrors);
    const schoolClass = ref();
    const loading = ref(false);
    const activeTab = ref("classInfo");

    return {
      userStore,
      studentStore,
      calendarStore,
      toastStore,
      modalStore,
      schoolClass,
      inputErrors,
      loading,
      activeTab,
    };
  },
  computed: {
    isEdit() {
      return this.schoolClass?.id != null;
    },
    schoolClassTeacher() {
      return this.schoolClass ? this.getMapping("teacher", this.schoolClass?.teacher) : undefined;
    },
    showOnlyOneStudentWarning(): boolean {
      // Show warning if only 1 student and class is not individual or pair class.
      return !this.schoolClass?.is_individual && !this.schoolClass?.is_pair && this.schoolClass?.students?.length === 1;
    },
    showManyStudentWarning(): boolean {
      // Show warning if many students and class is individual (not for pair classes)
      return !!this.schoolClass?.is_individual && !this.schoolClass?.is_pair && (this.schoolClass?.students?.length || 0) > 1;
    },
    tabs() {
      return [
        {
          key: "classInfo",
          title: "Class Info",
        },
        {
          key: "events",
          title: "Events",
        },
      ];
    },
  },
  mounted() {
    this.setSchoolClass(this.schoolClassProp);
    this.fetchSchoolClass();
  },
  created() {
    this.studentStore.resetState();
    this.studentStore.resetAttendanceStudents();
  },
  methods: {
    setSchoolClass(schoolClass?: Partial<ISchoolClass>) {
      this.schoolClass = new SchoolClass({
        id: schoolClass?.id,
        teacher: schoolClass?.teacher,
        title: schoolClass?.title,
        class_location: schoolClass?.class_location ?? this.getDefaultChoice("class_location"),
        class_type: schoolClass?.class_type ?? this.getDefaultChoice("class_type"),
        class_language: schoolClass?.class_language ?? this.getDefaultChoice("class_language"),
        level: schoolClass?.level ?? this.getDefaultChoice("level"),
        weekday: schoolClass?.weekday,
        time: schoolClass?.time ?? "12:00",
        created: schoolClass?.created,
        updated: schoolClass?.updated,
        is_active: schoolClass?.is_active,
        is_individual: schoolClass?.is_individual,
        is_pair: schoolClass?.is_pair,
        google_meets_url: schoolClass?.google_meets_url,
        company: schoolClass?.company,
        students: schoolClass?.students,
        student_names: schoolClass?.student_names,
      });
    },
    async fetchSchoolClass() {
      /**
       * The passed class can be stale (loaded a while ago), so fetch the latest
       * version before editing to not overwrite someone else's changes.
       **/
      if (!this.schoolClassProp?.id) return;

      this.loading = true;
      try {
        this.setSchoolClass(await api.calendarApi.getSchoolClass(this.schoolClassProp.id));
      } catch (error: any) {
        console.error(error);
      }
      await this.studentStore.getAttendanceStudents(this.schoolClass.students);
      this.loading = false;
    },
    confirmDeleteClass() {
      this.modalStore.openDeleteSchoolClassModal(this.schoolClass, this.deleteSchoolClass);
    },
    async save() {
      this.loading = true;
      try {
        await this.calendarStore.saveSchoolClass(this.schoolClass);
      } catch (error: any) {
        this.inputErrors = error;
        this.loading = false;
        return;
      }
      this.loading = false;

      this.close();
    },
    async deleteSchoolClass() {
      this.loading = true;
      try {
        await this.calendarStore.deleteSchoolClass(this.schoolClass);
      } catch (error: any) {
        this.inputErrors = error;
        this.loading = false;
        return;
      }
      this.loading = false;

      this.close();
    },
    close() {
      this.$emit("close");
    },
  },
});
</script>
<style scoped lang="scss">
.modal-body {
  overflow: auto;
}
@include sm {
  .modal-content {
    max-height: 90vh;
  }
}
@include lg {
  .modal-dialog.modal-xl {
    --bs-modal-width: 1140px;
  }
}
.class_event_time_range {
  gap: 12px;
  display: flex;
}
.attendance_table_wrapper {
  overflow: auto;
}
:deep(.start_date) {
  max-width: 100%;
}
</style>
